/**
 * The companion-first surface shell — wires the API client, the switcher + chat
 * stores, the modal surfaces (builder/wizard), the Artifacts gallery, the realtime
 * socket, and the hash router into the two-pane layout (switcher aside + her
 * stream). The active girlfriend is derived from the route; selecting one in the
 * switcher navigates the hash → the store opens her thread and tails her run.
 *
 * Composition root for the product surface: it is the only place that constructs
 * the concrete stores (DIP — everything below takes them as props). No AI logic,
 * no SQL, no direct network — it drives `ApiClient`, renders `@4f/ui`, and routes
 * the agent's generative-UI directives (`modal`/`gallery`) to real surfaces.
 */
import type { Locale } from '@4f/i18n';
import { isOk, type UiSurface } from '@4f/shared';
import {
  Button,
  createToastStore,
  Drawer,
  formatDateTime,
  Spinner,
  ToastRegion,
  useT,
} from '@4f/ui';
import {
  createEffect,
  createMemo,
  createSignal,
  type JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import { WS_PATH } from '../api/endpoints.ts';
import type { CompanionView } from '../api/types.ts';
import { type BuilderChoice, builderDestination } from '../features/builder/builder-picker.ts';
import { ChatView } from '../features/chat/ChatView.tsx';
import { createAvatarCache } from '../features/companions/avatar-cache.ts';
import { ArtifactsGallery } from '../features/gallery/ArtifactsGallery.tsx';
import { createAssetUrlCache } from '../features/gallery/asset-url-cache.ts';
import { SurfaceHost } from '../features/surface/SurfaceHost.tsx';
import { Switcher } from '../features/switcher/Switcher.tsx';
import { browserPermission, browserPushManager, registerPushSubscription } from '../pwa/push.ts';
import { registerServiceWorkerUpdates } from '../pwa/register.ts';
import { initServiceWorkerUpdates } from '../pwa/update-controller.ts';
import { createRealtime, mediaJobLanded, type RealtimeHandlers } from '../realtime/ws.ts';
import { useRouter } from '../router/RouterProvider.tsx';
import { createBillingStore } from '../stores/billing.ts';
import { createBuilderStore } from '../stores/builder.ts';
import { createChatStore } from '../stores/chat.ts';
import { createCompanionsStore } from '../stores/companions.ts';
import { createBlobArchiveSaver, createDataRightsStore } from '../stores/data-rights.ts';
import { createGalleryStore } from '../stores/gallery.ts';
import { createPrebuiltStore } from '../stores/prebuilt.ts';
import { createReferralStore } from '../stores/referral.ts';
import type { SessionStore } from '../stores/session.ts';
import type { SettingsStore } from '../stores/settings.ts';
import { createSurfaceStore } from '../stores/surface.ts';

export interface CompanionSurfaceProps {
  readonly timeZone: string;
  readonly locale: Locale;
  /** The typed edge client, wired at the composition root (`App`) — DIP. */
  readonly api: ApiClient;
  /** The in-memory session-token supplier the api client + `/ws` read. */
  readonly token: () => string | null;
  /** The auth session — drives the sign-out control. */
  readonly session: SessionStore;
  /** User settings (theme/locale/tz) — the settings surface edits, the shell reads. */
  readonly settings: SettingsStore;
}

/** Heading ids for the two switcher instances (desktop aside + mobile drawer). */
const DESKTOP_SWITCHER_TITLE_ID = 'f4-switcher-title-desktop';
const DRAWER_SWITCHER_TITLE_ID = 'f4-switcher-title-drawer';

/** Trigger a browser download of a presigned asset URL via a transient anchor. */
function downloadUrl(url: string, filename: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.target = '_blank';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function CompanionSurface(props: CompanionSurfaceProps): JSX.Element {
  const { t } = useT();
  const router = useRouter();

  const api = props.api;
  const companions = createCompanionsStore(api);
  const chat = createChatStore(api);
  const toasts = createToastStore();
  const surface = createSurfaceStore();
  const gallery = createGalleryStore(api);
  const builder = createBuilderStore(api);
  const prebuilt = createPrebuiltStore(api);
  const billing = createBillingStore(api);
  // Self-serve export + delete. The session is NOT dropped automatically on a
  // successful deletion: `session.logout()` clears `user()`, which unmounts this
  // whole subtree (AuthGate swaps in the sign-in surface) — including the modal
  // showing the purge date the user can never look up again. The panel renders
  // the schedule and offers sign-out; `onSignOut` below is that click.
  const dataRights = createDataRightsStore(api, { saver: createBlobArchiveSaver() });
  const referral = createReferralStore(api);
  const avatars = createAvatarCache(api);
  // Resolves the opaque asset ids a `gallery` directive carries to fetchable URLs
  // (without it those tiles render `<img src={undefined}>`).
  const assetUrls = createAssetUrlCache(api);

  const active = createMemo(() => companions.active(router.route().companionId));

  // Resolve avatars for whatever the switcher currently shows (and the active
  // companion, which may not yet be in `visible()` right after creation) — each
  // companion is fetched + cached at most once (`avatars.ensure` is idempotent).
  createEffect(() => {
    for (const companion of companions.visible()) {
      avatars.ensure(companion);
    }
    const current = active();
    if (current) {
      avatars.ensure(current);
    }
  });

  // Realtime: proactive delivery + cross-device sync + credit push (Toast). The
  // socket only opens once a session token exists (pre-auth it is a no-op).
  const realtimeHandlers: RealtimeHandlers = {
    onProactiveMessage(message) {
      toasts.add({
        title: message.title,
        message: message.body ?? t('surface.proactiveFrom'),
        variant: 'info',
      });
      void companions.reload();
      if (chat.conversationId() === message.conversationId) {
        void chat.refresh();
      }
    },
    // Routed by the thread's ID, never by the loaded conversation VIEW: that one is
    // `null` whenever its history read failed (an offline reopen), and gating on it
    // drops the push for exactly the thread whose `refresh` would repair it.
    onMessageCreated(message) {
      if (chat.conversationId() === message.conversationId) {
        void chat.refresh();
      }
    },
    onTitleUpdated(message) {
      chat.applyTitle(message.conversationId, message.title);
    },
    onCreditUpdated() {
      toasts.add({ message: t('surface.creditsUpdated'), variant: 'warning' });
    },
    onMediaJobUpdate(message) {
      void chat.resolveMediaJob(message);
      // New media landed — re-index her assets so inline gallery tiles resolve.
      // Gated: the worker also beats `running` + one `segment_done` per video
      // segment, and re-reading her whole gallery on each would be ~14 pointless
      // presign round-trips per clip, in every open tab.
      if (mediaJobLanded(message)) {
        assetUrls.refresh(message.girlfriendId);
      }
    },
  };
  // `/ws` is NEVER prefixed with the api base — the ingress routes the upgrade
  // bare, without the stripPrefix middleware (see `api/endpoints.ts`).
  const realtime = createRealtime({
    url: WS_PATH,
    token: props.token,
    handlers: realtimeHandlers,
    // The edge rejects an upgrade with `401` BEFORE it upgrades, and a browser
    // cannot read the status of a failed handshake — it arrives as `error` +
    // close `1006`, identical to a network drop. So a socket whose token expired
    // would re-dial that same dead token for the life of the tab: no proactive
    // messages, no cross-device sync, no media-completion beat, with the UI still
    // looking healthy. Renew before re-dialling a refused handshake — through the
    // api client's SINGLE-FLIGHT renewal, so a socket retry and an HTTP `401`
    // recovery can never fire two concurrent refreshes (the edge treats a
    // superseded refresh token as reuse and revokes the whole family).
    renew: async () => (await api.renewSession()) === 'renewed',
  });

  onMount(() => {
    void companions.reload();
    realtime.connect();
    // Wire the phase-0 build-hash → controlled-reload PWA update flow: a newly
    // installed SW (new build hash ⇒ new precache) surfaces an "update available"
    // Toast; accepting skipWaiting()s the worker and reloads onto fresh assets.
    initServiceWorkerUpdates({
      register: registerServiceWorkerUpdates,
      toasts,
      messages: {
        available: t('surface.update.available'),
        body: t('surface.update.body'),
        reload: t('surface.update.reload'),
        failed: t('surface.update.failed'),
      },
    });
    // Register this device for Web Push so a proactive message reaches a CLOSED
    // tab. Consent-safe: it only registers when notification permission was
    // already granted (no `askPermission` is passed), so it never prompts on
    // load — the explicit ask belongs to a user-initiated control.
    void registerPushSubscription({
      pushManager: browserPushManager,
      permission: browserPermission,
      serverKey: import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '',
      save: async (subscription) => isOk(await api.subscribePush(subscription)),
    });
  });
  // Reconnect → replay whatever was typed offline. `online` is the browser's own
  // "the network came back" signal; without this the queue would only drain on
  // the next manual send.
  //
  // It also re-dials the socket. The reconnect ladder only resets on a socket
  // that OPENED, so an outage long enough to reach its tail (sleep, tunnel, hotel
  // wifi) would otherwise leave the user up to a full tail-interval with no
  // proactive delivery, no cross-device sync and no credit toasts after the
  // network was already back. `connect()` is a no-op while a socket is live, so
  // a spurious `online` costs nothing.
  onMount(() => {
    const flush = (): void => {
      void chat.flushOutbox();
      realtime.connect();
    };
    globalThis.addEventListener?.('online', flush);
    onCleanup(() => globalThis.removeEventListener?.('online', flush));
    // A queue also arrives WITH the app: the tab was closed (or killed) while
    // offline, and `online` never fires for a session that starts connected. The
    // replay is conversation-agnostic, so it drains those turns before any
    // companion is even picked — otherwise they wait for a network flap that may
    // never come.
    void chat.flushOutbox();
  });

  onCleanup(() => {
    chat.dispose();
    realtime.close();
  });

  // Active companion changed (route or list load) → open her thread + sync the hash.
  // Her Artifacts lightbox belongs to her, so close it on a switch (never show one
  // companion's media under another's name).
  createEffect(
    on(active, (current, previous) => {
      if (current && current.id !== previous?.id) {
        gallery.close();
        // A PREBUILT companion's media was pre-generated offline, so her reveals
        // run through the faked "generating…" beat — presentation only, never a
        // difference in what ran or what was billed.
        void chat.openCompanion(current.id, { prebuilt: current.buildSource === 'prebuilt' });
        assetUrls.ensure(current.id);
        if (router.route().companionId !== current.id) {
          router.navigate({ companionId: current.id });
        }
      }
    }),
  );

  const [menuOpen, setMenuOpen] = createSignal(false);
  const select = (companionId: string): void => {
    router.navigate({ companionId });
    setMenuOpen(false);
  };

  // --- Modal surfaces (builder/wizard) --------------------------------------
  const openSurface = (which: UiSurface): void => {
    builder.reset();
    surface.open(which);
  };
  const onCompanionCreated = (companion: CompanionView): void => {
    surface.close();
    toasts.add({
      message: t('surface.companionReady', { companionName: companion.name }),
      variant: 'success',
    });
    void companions.reload();
    router.navigate({ companionId: companion.id });
  };
  // Funnel routing for the builder's picker: custom generation is paid upfront →
  // open billing IMMEDIATELY (the paywall front-loads before any build); prebuilt
  // is the free start → the builder surface proceeds to account setup in place.
  const onBuilderChoose = (choice: BuilderChoice): void => {
    if (builderDestination(choice) === 'billing') {
      surface.open('billing');
    }
  };

  // --- Artifacts gallery -----------------------------------------------------
  const openGallery = async (startAssetId?: string): Promise<void> => {
    const companion = active();
    if (!companion) {
      return;
    }
    const result = await gallery.openFor(companion.id, startAssetId);
    if (!result.opened) {
      toasts.add({
        message: gallery.error() ? t('surface.galleryError') : t('gallery.empty'),
        variant: gallery.error() ? 'danger' : 'info',
      });
    }
  };
  const setAvatar = async (): Promise<void> => {
    const result = await gallery.setCurrentAsAvatar();
    if (!result) {
      return;
    }
    if (isOk(result)) {
      toasts.add({ message: t('surface.avatarUpdated'), variant: 'success' });
      void companions.reload();
      gallery.close();
    } else {
      toasts.add({ message: t('surface.avatarFailed'), variant: 'danger' });
    }
  };

  // The panel is rendered twice — a persistent desktop aside and the mobile
  // drawer — so each instance gets its OWN heading id (duplicate ids are invalid
  // HTML and make the drawer's `aria-labelledby` ambiguous).
  const switcher = (headingId: string): JSX.Element => (
    <div class="f4-switcher-panel">
      <Switcher
        companions={companions.visible()}
        activeId={active()?.id ?? null}
        loading={companions.loading()}
        onSelect={select}
        avatarSrc={avatars.srcFor}
        headingId={headingId}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        class="f4-switcher-panel__new"
        onClick={() => openSurface('builder')}
      >
        {t('surface.newCompanion')}
      </Button>
      {/* This nav holds Settings + Billing + Referrals + Sign-out — labelling it
          "Settings" misnames three of its four destinations for a screen reader. */}
      <nav class="f4-switcher-panel__nav" aria-label={t('surface.accountNav')}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          class="f4-switcher-panel__navItem"
          onClick={() => openSurface('settings')}
        >
          {t('surface.surfaces.settings')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          class="f4-switcher-panel__navItem"
          onClick={() => openSurface('billing')}
        >
          {t('surface.surfaces.billing')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          class="f4-switcher-panel__navItem"
          onClick={() => openSurface('referral')}
        >
          {t('surface.surfaces.referral')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          class="f4-switcher-panel__navItem"
          onClick={() => props.session.logout()}
        >
          {t('auth.signOut')}
        </Button>
      </nav>
    </div>
  );

  return (
    <div class="f4-surface">
      <aside class="f4-surface__aside">{switcher(DESKTOP_SWITCHER_TITLE_ID)}</aside>

      {/* `labelledBy` names the dialog from the switcher heading inside it —
          without it the mobile drawer is an UNNAMED dialog to a screen reader. */}
      <Drawer
        open={menuOpen()}
        onClose={() => setMenuOpen(false)}
        side="left"
        labelledBy={DRAWER_SWITCHER_TITLE_ID}
        closeLabel={t('surface.close')}
      >
        {switcher(DRAWER_SWITCHER_TITLE_ID)}
      </Drawer>

      <main class="f4-surface__main">
        <Show
          when={active()}
          fallback={
            <SurfaceEmpty loading={companions.loading()} onCreate={() => openSurface('builder')} />
          }
        >
          {(companion) => (
            <ChatView
              store={chat}
              companion={companion()}
              avatarSrc={avatars.srcFor(companion().id)}
              timeZone={props.timeZone}
              locale={props.locale}
              onOpenMenu={() => setMenuOpen(true)}
              onOpenArtifacts={() => void openGallery()}
              onOpenGallery={(assetIds, startIndex) =>
                void openGallery(assetIds[startIndex] ?? assetIds[0])
              }
              onOpenSurface={openSurface}
              assetUrl={assetUrls.urlFor}
            />
          )}
        </Show>
      </main>

      <SurfaceHost
        surface={surface}
        builder={builder}
        prebuilt={prebuilt}
        billing={billing}
        referral={referral}
        settings={props.settings}
        dataRights={dataRights}
        formatDate={(iso) =>
          formatDateTime(iso, { timeZone: props.timeZone, locale: props.locale, part: 'date' })
        }
        onSignOut={() => props.session.logout()}
        origin={globalThis.location?.origin ?? ''}
        onCompanionCreated={onCompanionCreated}
        onBuilderChoose={onBuilderChoose}
      />
      <Show when={active()}>
        {(companion) => (
          <ArtifactsGallery
            store={gallery}
            companionName={companion().name}
            onDownload={downloadUrl}
            onSetAvatar={() => void setAvatar()}
          />
        )}
      </Show>
      <ToastRegion store={toasts} dismissLabel={t('surface.dismiss')} />
    </div>
  );
}

/** Shown when the account has no (visible) companion yet — or while the list loads. */
function SurfaceEmpty(props: {
  readonly loading: boolean;
  readonly onCreate: () => void;
}): JSX.Element {
  const { t } = useT();
  return (
    <div class="f4-surface__empty">
      <Show when={props.loading} fallback={<EmptyCta onCreate={props.onCreate} />}>
        <Spinner label={t('surface.loadingCompanions')} />
      </Show>
    </div>
  );
}

/** First-run call to action — opens the builder/wizard modal. */
function EmptyCta(props: { readonly onCreate: () => void }): JSX.Element {
  const { t } = useT();
  return (
    <div class="f4-surface__cta">
      <p>{t('chat.switcher.empty')}</p>
      <Button type="button" variant="primary" onClick={() => props.onCreate()}>
        {t('surface.createFirst')}
      </Button>
    </div>
  );
}
